import React, { Fragment, useState, useEffect } from "react";
import { Col, Card, Modal, Button, Form, Spinner } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import Snackbars from "../../../utils/Snackbars";
import useRequests from "../../../utils/useRequests";
import DataTable from "react-data-table-component";

const VenueList = () => {
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [modalType, setModalType] = useState("");
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusProcessing, setStatusProcessing] = useState(false);
    const [deleteProcessing, setDeleteProcessing] = useState(false);
    const { getRequest, deleteRequest, putRequest } = useRequests();
    const [searchQuery, setSearchQuery] = useState('');

    const [AlertData, setAlertData] = React.useState({
        variant: '',
        message: '',
    });

    const resetAlertData = () => {
        setTimeout(() => {
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: '',
                message: '',
            }));
        }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
    };

    const fetchVenues = async () => {
        setLoading(true);
        try {
            const response = await getRequest("venue-list");
            if (response && response.code === 200) {
                setVenues(response.data);
            } else {
                console.error("Failed to fetch venues:", response?.message);
                setAlertData((AlertData) => ({
                    ...AlertData,
                    variant: 'error',
                    message: 'Failed to fetch venues',
                }));
                resetAlertData();
            }
        } catch (error) {
            console.error("Error fetching venues:", error);
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: 'error',
                message: 'Error fetching venues',
            }));
            resetAlertData();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVenues();
        if (location.state && location.state.message) {
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: location.state.variant,
                message: location.state.message,
            }));
            resetAlertData();
            setLoading(false);
        }
    }, []);

    // Filter venues based on the search query
    const filteredVenues = venues.filter((venue) => {
        const name = `${venue.name}`.toLowerCase();
        const city = `${venue.city}`.toLowerCase();
        const status = `${venue.status}`.toLowerCase();
        const searchLower = searchQuery.toLowerCase();

        return name.includes(searchLower) || city.includes(searchLower) || status.includes(searchLower);
    });

    const handleOpenModal = (venue) => {
        setSelectedVenue(venue);
        setModalType(venue.status === "Enable" ? "disable" : "enable");
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalType("");
        setSelectedVenue(null);
        setStatusProcessing(false);
    };

    const handleConfirmAction = async () => {
        if (selectedVenue) {
            const newStatus = modalType === "enable" ? "Enable" : "Disable";
            setStatusProcessing(true);

            try {
                const jsonpar = JSON.stringify({ status: newStatus });
                const response = await putRequest(`venue-status/${selectedVenue.v_id}`, jsonpar);

                if (response && response.code === 200) {
                    const updatedVenues = venues.map((venue) =>
                        venue.v_id === selectedVenue.v_id ? { ...venue, status: newStatus } : venue
                    );
                    setVenues(updatedVenues);
                    setAlertData((AlertData) => ({
                        ...AlertData,
                        variant: 'success',
                        message: `Venue ${newStatus.toLowerCase()}d successfully`,
                    }));
                    resetAlertData();
                } else {
                    console.error("Failed to update status:", response?.message);
                    setAlertData((AlertData) => ({
                        ...AlertData,
                        variant: 'error',
                        message: `Failed to ${modalType} venue.`,
                    }));
                    resetAlertData();
                }
            } catch (error) {
                console.error("Error updating status:", error);
                setAlertData((AlertData) => ({
                    ...AlertData,
                    variant: 'error',
                    message: 'Error updating status',
                }));
                resetAlertData();
            } finally {
                setStatusProcessing(false);
            }
        }
        handleCloseModal();
    };

    const handleDeleteVenue = async () => {
        if (selectedVenue) {
            setDeleteProcessing(true);
            
            try {
                const response = await deleteRequest(`venue-delete/${selectedVenue.v_id}`);
                if (response && response.code === 200) {
                    const updatedVenues = venues.filter((venue) => venue.v_id !== selectedVenue.v_id);
                    setVenues(updatedVenues);
                    setAlertData((AlertData) => ({
                        ...AlertData,
                        variant: 'success',
                        message: 'Venue deleted successfully!',
                    }));
                    resetAlertData();
                } else {
                    console.error("Failed to delete venue:", response?.message);
                    setAlertData((AlertData) => ({
                        ...AlertData,
                        variant: 'error',
                        message: 'Failed to delete venue',
                    }));
                    resetAlertData();
                }
            } catch (error) {
                console.error("Error deleting venue:", error);
                setAlertData((AlertData) => ({
                    ...AlertData,
                    variant: 'error',
                    message: 'Error deleting venue',
                }));
                resetAlertData();
            } finally {
                setDeleteProcessing(false);
                setShowDeleteModal(false);
                setSelectedVenue(null);
            }
        }
    };

    const openDeleteModal = (venue) => {
        setSelectedVenue(venue);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedVenue(null);
        setDeleteProcessing(false);
    };

    const columns = [
        {
            name: "Venue Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "City",
            selector: (row) => row.city,
            sortable: true,
        },
        {
            name: "Capacity",
            selector: (row) => row.capacity,
            sortable: true,
            width: "120px"
        },
        {
            name: "Status",
            cell: (row) => (
                <button
                    onClick={() => handleOpenModal(row)}
                    className={`btn ${
                        row.status === "Enable" ? "btn-success" : "btn-danger"
                    } btn-xxs`}
                    disabled={statusProcessing || deleteProcessing}
                >
                    {row.status === "Enable" ? "Enable" : "Disable"}
                </button>
            ),
            center: true,
            width: "150px",
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="text-center">
                    <Link 
                        className={`btn btn-primary btn-xxs me-2`} 
                        to={`/venue-edit/${row.v_id}`}
                        style={{ 
                            pointerEvents: (statusProcessing || deleteProcessing) ? 'none' : 'auto',
                            opacity: (statusProcessing || deleteProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-regular fa-pen-to-square"></i>
                    </Link>
                    <Link 
                        to={`/venue-details/${row.v_id}`} 
                        className={`btn btn-primary btn-xxs me-2`}
                        style={{ 
                            pointerEvents: (statusProcessing || deleteProcessing) ? 'none' : 'auto',
                            opacity: (statusProcessing || deleteProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-regular fa-eye"></i>
                    </Link>
                    <Link 
                        to={`/venue-seat-categories/${row.v_id}`} 
                        className={`btn btn-info btn-xxs me-2`}
                        style={{ 
                            pointerEvents: (statusProcessing || deleteProcessing) ? 'none' : 'auto',
                            opacity: (statusProcessing || deleteProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="flaticon-381-controls"></i>
                    </Link>
                    <button 
                        className="btn btn-danger btn-xxs" 
                        onClick={() => openDeleteModal(row)}
                        disabled={statusProcessing || deleteProcessing}
                    >
                        <i className="fa-solid fa-trash"></i>
                    </button>
                </div>
            ),
            center: true,
            width: "220px",
        },
    ];

    const customStyles = {
        header: {
            style: {
                whiteSpace: "normal",
            },
        },
        headCells: {
            style: {
                color: "#341439",
                fontSize: "17px",
                fontWeight: "600",
                textTransform: "capitalize",
                paddingLeft: "8px",
                paddingRight: "8px",
            },
        },
        cells: {
            style: {
                fontSize: "16px",
                padding: "12px 9px",
                color: "#7E7E7E",
            },
        },
        pagination: {
            style: {
                fontSize: "16px",
            },
        },
    };

    return (
        <Fragment>
            <div className='d-sm-flex d-block justify-content-between align-items-center mb-4'>
                <h2 className='text-black font-w600 mb-0'>
                    <i className="flaticon-381-location me-2"></i>
                    Venue Management
                </h2>
                {AlertData.message !== "" ? (
                    <Snackbars
                        variant={AlertData.variant}
                        message={AlertData.message}
                    />
                ) : (
                    ""
                )}
                <Link
                    to='/venue-add'
                    type='button'
                    className='btn btn-rounded btn-primary'
                    style={{ 
                        pointerEvents: (statusProcessing || deleteProcessing) ? 'none' : 'auto',
                        opacity: (statusProcessing || deleteProcessing) ? 0.6 : 1 
                    }}
                >
                    <i className="flaticon-381-location me-2"></i>
                    Add Venue
                </Link>
            </div>

            {/* Search input */}
            <div className='mb-4 d-sm-flex d-block justify-content-end'>
                <div style={{ position: "relative", width: "18rem" }}>
                    <Form.Control
                        type='text'
                        placeholder='Search venues...'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={statusProcessing || deleteProcessing}
                        style={{
                            width: "100%",
                            fontSize: "18px",
                            fontWeight: 500,
                            paddingRight: "2rem",
                            borderRadius: "0.625rem",
                            height: "3.2rem",
                            opacity: (statusProcessing || deleteProcessing) ? 0.6 : 1
                        }}
                    />
                    <i
                        className='fa fa-search'
                        style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#6c757d",
                            pointerEvents: "none",
                        }}
                    ></i>
                </div>
            </div>

            <Col lg={12}>
                <Card>
                    <Card.Body>
                        {loading ? (
                            <div className='text-center'>
                                <Spinner animation='border' variant="primary" />
                                <p>Loading venues...</p>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredVenues}
                                customStyles={customStyles}
                                pagination
                                highlightOnHover
                                responsive
                                progressPending={statusProcessing || deleteProcessing}
                                progressComponent={
                                    <div className="text-center p-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2">
                                            {statusProcessing ? "Updating status..." : "Deleting venue..."}
                                        </p>
                                    </div>
                                }
                            />
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {/* Enable/Disable Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === "enable" ? "Enable Venue" : "Disable Venue"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to {modalType} the venue{" "}
                    <strong>{selectedVenue?.name}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant='light' 
                        onClick={handleCloseModal}
                        disabled={statusProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={`btn btn-${
                            modalType === "enable" ? "success" : "danger"
                        }`}
                        onClick={handleConfirmAction}
                        disabled={statusProcessing}
                    >
                        {statusProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing...
                            </>
                        ) : (
                            modalType === "enable" ? "Enable" : "Disable"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Venue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the venue{" "}
                    <strong>{selectedVenue?.name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant='light' 
                        onClick={closeDeleteModal}
                        disabled={deleteProcessing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant='danger' 
                        onClick={handleDeleteVenue}
                        disabled={deleteProcessing}
                    >
                        {deleteProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default VenueList;